<template>
  <section>
    <header>
      <h3>User Schema Attribute Mapping</h3>
      <p>
        This section is where you will define the user atributes returned from the service
        provider and map those values to the corresponding JumpCloud attributes.
        <strong v-if="!showMappings"> {{ noticeText }}</strong>
      </p>
      <p v-if="showMappings">
        Specify the JSON paths for the unique ID, user status, and the user attributes
        you want to map to JumpCloud from the service provider.
      </p>
    </header>
    <div v-if="showMappings">
      <div>
        <InputValidator
          v-model="userSchemaIdPath"
          :config="{
            label: 'Unique ID *',
            name: 'unique id path',
            value: userSchemaIdPath,
            validate: 'required',
            attribute: 'unique id path',
          }"
          data-test-id="IdMCustomAttributeMapping_uniqueId"
          @InputValidatorModel="setUserSchemaIdPath"
        />
        <p :class="$style.supportingText">
          Please provide a JSON path for location of the schema unique ID.
        </p>
        <InputValidator
          v-model="inactiveStatus.path"
          :config="{
            label: 'User Status',
            name: 'user status path',
            value: inactiveStatus.path,
            attribute: 'user status path',
          }"
          data-test-id="IdMCustomAttributeMapping_userStatusPath"
          @InputValidatorModel="handleUserSchemaPath"
        />
        <p :class="$style.supportingText">
          Please provide a JSON path for location of the user's status. User status may also be
          referred to as user state depending on the service provider.
        </p>
        <InputValidator
          v-model="inactiveStatus.value"

          :config="{
            label: 'Inactive Status Values',
            name: 'inactive status values path',
            value: inactiveStatus.values,
            attribute: 'inactive status values path',
          }"
          data-test-id="IdMCustomAttributeMapping_userStatusValues"
          @InputValidatorModel="handleInactiveStatus"
        />
        <p :class="$style.supportingText">
          Please provide all values from the service provider that map to an inactive user status.
          Use a comma to separate multiple values.
        </p>
      </div>
      <div
        v-if="false"
        :class="[$style.flexContainer, $style.schemaUrlContainer]"
      >
        <!--
      This has been removed from Custom REST API MVP but it
      will be added in the future
      -->
        <div :class="$style.schemaUrlInputContainer">
          <InputValidator
            v-model="schemaUrl"
            :config="{
              label: 'Schema URL',
              name: 'schema url',
              value: schemaUrl,
              validate: {
                url: { require_protocol: true },
                required: true,
              },
              placeholder: 'https://some-path.com/here',
              attribute: 'schema url',
            }"
            data-test-id="IdMCustomAttributeMapping_schemaUrl"
            @InputValidatorModel="handlePopulateUrlChange"
          />
        </div>
        <div :class="$style.schemaUrlButton">
          <FlatActionButton
            :action="populate"
            :buttonClass="['btnDefault', 'btnCTA']"
            :class="$style.populateButton"
            data-test-id="IdMCustomAttributeMapping_populate"
            :disabled="isPopulateDisabled"
            text="Populate"
          />
        </div>
      </div>
      <AttributeMappingHeader :enablePreview="enablePreview" />
      <GenericScimConnectorErrors
        :testConnectionErrorsCategoryFilter="SCHEMA"
      />
      <RequiredAttributesMapping />
      <OptionalAttributesMapping />
    </div>
  </section>
</template>

<script>
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { authenticationMethods, verifyTypes } from 'client/util/Constants/ApplicationConstants';
import { getMissingStepsNoticeText } from 'client/applications/utils/ApplicationUtils';
import { mapActions, mapState } from 'vuex';
import AttributeMappingHeader from 'client/applications/scim/customConnector/AttributeMappingHeader.vue';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import GenericScimConnectorErrors from 'client/applications/scim/GenericScimConnectorErrors.vue';
import IdentityManagementMixin from 'client/applications/mixins/IdentityManagementMixin';
import InputValidator from 'client/components/FormValidation/InputValidator.vue';
import OptionalAttributesMapping from 'client/applications/scim/customConnector/OptionalAttributesMapping.vue';
import RequiredAttributesMapping from 'client/applications/scim/customConnector/RequiredAttributesMapping.vue';

const { application } = ItemTypeConsts;
const applicationModel = StoreModelMap[application];

const { OAUTH2 } = authenticationMethods;
const { SCHEMA } = verifyTypes;

export default {
  name: 'AttributeMappingSection',

  components: {
    AttributeMappingHeader,
    FlatActionButton,
    InputValidator,
    OptionalAttributesMapping,
    RequiredAttributesMapping,
    GenericScimConnectorErrors,
  },

  inject: ['$validator'],

  mixins: [
    IdentityManagementMixin,
  ],

  data() {
    return {
      SCHEMA,
      schemaUrl: '',
      path: '',
      values: '',
    };
  },

  computed: {
    ...mapState(applicationModel, ['userSchemaIdPath', 'inactiveStatus']),

    enablePreview() {
      const email = this.$validator.flags['Company email'];
      const username = this.$validator.flags.Username;
      return email && email.valid && username && username.valid;
    },

    isPopulateDisabled() {
      const schemaUrl = this.$validator.flags['schema url'];
      return schemaUrl && schemaUrl.invalid;
    },

    noticeText() {
      return getMissingStepsNoticeText(
        this.isOAuth2 ? OAUTH2 : '',
      );
    },

    showMappings() {
      return this.isOAuth2
        ? this.isIdmOauthConfigured || this.isScimActive : this.isTestConnectionPassed;
    },
  },

  created() {
    this.path = this.inactiveStatus.path;
    this.values = this.inactiveStatus.values;
  },

  methods: {
    ...mapActions(applicationModel, [
      'setUserSchemaIdPath',
      'setUserSchemaInactiveStatus',
      'populateCustomApiOptionalMapping',
    ]),

    handleInactiveStatus(newValues) {
      this.values = newValues;
      this.setUserSchemaInactiveStatus({ path: this.path, values: this.values });
    },

    handlePopulateUrlChange(url) {
      this.schemaUrl = url;
    },

    handleUserSchemaPath(newPath) {
      this.path = newPath;
      this.setUserSchemaInactiveStatus({ path: this.path, values: this.values });
    },

    async populate() {
      await this.populateCustomApiOptionalMapping(this.schemaUrl);
    },
  },
};
</script>

<style module>
@import '../css/IdentityManagementCommon.css';

.schemaUrlInputContainer {
  flex: 1 2;
}

.schemaUrlButton {
  flex: 0 1;
  margin-left: 1rem;
}

.supportingText {
  color: jcGrey;
  font-size: 12px;
}

.populateButton {
  line-height: 1.5;
  margin-top: 2rem;
}
</style>
